import { computed, onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import Swal from "sweetalert2";

import reportService, { type SupplierReportItem } from "@/services/report";
import supplierService, { type Supplier } from "@/services/supplier";
import warehouseService, { type Warehouse } from "@/services/warehouse";

type SupplierReportFilters = {
  warehouse_id: number | null;
  supplier_id: number | null;
  status: string;
  from_date: string;
  to_date: string;
  per_page: number;
};

const createInitialFilters = (): SupplierReportFilters => ({
  warehouse_id: null,
  supplier_id: null,
  status: "",
  from_date: "",
  to_date: "",
  per_page: 10,
});

export function useSupplierReport() {
  const { t } = useI18n();
  const items = ref<SupplierReportItem[]>([]);
  const warehouses = ref<Warehouse[]>([]);
  const suppliers = ref<Supplier[]>([]);
  const loading = ref(false);
  const exporting = ref(false);
  const searchQuery = ref("");
  const currentPage = ref(1);
  const total = ref(0);
  const filters = reactive<SupplierReportFilters>(createInitialFilters());
  const i18nScope = "supplier_report_page";
  const pageTitle = `${i18nScope}.title`;

  const pagination = computed(() => ({
    current: currentPage.value,
    pageSize: filters.per_page,
    total: total.value,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "50", "100"],
    showTotal: (total: number, range: [number, number]) =>
      t(`${i18nScope}.pagination_total`, { start: range[0], end: range[1], total }),
    locale: { items_per_page: t(`${i18nScope}.items_per_page`) },
  }));

  const queryParams = computed(() => ({
    page: currentPage.value,
    per_page: filters.per_page,
    search_query: searchQuery.value || undefined,
    warehouse_id: filters.warehouse_id || undefined,
    supplier_id: filters.supplier_id || undefined,
    status: filters.status || undefined,
    from_date: filters.from_date || undefined,
    to_date: filters.to_date || undefined,
  }));

  const fetchFilterOptions = async () => {
    try {
      const [warehouseResults, supplierResults] = await Promise.all([
        warehouseService.getWarehouses(),
        supplierService.getSuppliers({ per_page: 1000 }),
      ]);

      warehouses.value = warehouseResults;
      suppliers.value = supplierResults.data ?? [];
    } catch (error) {
      console.error("Error loading supplier report filters:", error);
    }
  };

  const fetchSupplierReports = async () => {
    loading.value = true;
    try {
      const response = await reportService.getSupplierReports(queryParams.value);
      items.value = response.data ?? [];
      currentPage.value = response.current_page;
      total.value = response.total;
    } catch (error) {
      console.error("Error fetching supplier reports:", error);
      Swal.fire({
        icon: "error",
        title: t(`${i18nScope}.load_failed`),
        text: t(`${i18nScope}.unable_to_load_report`),
      });
    } finally {
      loading.value = false;
    }
  };

  const handleTableChange = (page: number, pageSize: number) => {
    currentPage.value = page;

    if (pageSize !== filters.per_page) {
      filters.per_page = pageSize;
      currentPage.value = 1;
    }

    fetchSupplierReports();
  };

  const applyFilters = () => {
    currentPage.value = 1;
    fetchSupplierReports();
  };

  const resetFilters = () => {
    Object.assign(filters, createInitialFilters());
    currentPage.value = 1;
    fetchSupplierReports();
  };

  const exportSupplierReports = async () => {
    exporting.value = true;
    try {
      const response = await reportService.exportSupplierReports({
        search_query: searchQuery.value || undefined,
        warehouse_id: filters.warehouse_id || undefined,
        supplier_id: filters.supplier_id || undefined,
        status: filters.status || undefined,
        from_date: filters.from_date || undefined,
        to_date: filters.to_date || undefined,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `supplier_report_${new Date().getTime()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting supplier reports:", error);
      Swal.fire({
        icon: "error",
        title: t(`${i18nScope}.export_failed`),
        text: t(`${i18nScope}.unable_to_export_report`),
      });
    } finally {
      exporting.value = false;
    }
  };

  watch(searchQuery, () => {
    currentPage.value = 1;
    fetchSupplierReports();
  });

  onMounted(async () => {
    await fetchFilterOptions();
    await fetchSupplierReports();
  });

  return {
    pageTitle,
    i18nScope,
    items,
    warehouses,
    suppliers,
    loading,
    exporting,
    searchQuery,
    filters,
    pagination,
    handleTableChange,
    applyFilters,
    resetFilters,
    exportSupplierReports,
  };
}
